import { FC, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { useEditor } from './EditorContext';

export type ToolType = 'bold' | 'italic' | 'ordered-list' | 'unordered-list' | null;

const applyBold = (cm: any) => {
  const selection = cm.getSelection();
  const startPos = cm.getCursor('start');
  const endPos = cm.getCursor('end');

  const line = cm.getLine(startPos.line);
  const beforeStart2 = line.substring(Math.max(0, startPos.ch - 2), startPos.ch);
  const afterEnd2 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 2));
  const beforeStart3 = line.substring(Math.max(0, startPos.ch - 3), startPos.ch);
  const afterEnd3 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 3));

  const isBoldItalic = beforeStart3 === '***' && afterEnd3 === '***';
  const isBold =
    (beforeStart2 === '**' && afterEnd2 === '**') ||
    (selection.startsWith('**') && selection.endsWith('**') && selection.length > 4);

  if (isBoldItalic) {
    cm.replaceRange(
      `*${selection}*`,
      { line: startPos.line, ch: startPos.ch - 3 },
      { line: endPos.line, ch: endPos.ch + 3 },
    );
    cm.setSelection(
      { line: startPos.line, ch: startPos.ch - 2 },
      { line: endPos.line, ch: startPos.ch - 2 + selection.length },
    );
  } else if (isBold) {
    if (beforeStart2 === '**' && afterEnd2 === '**') {
      cm.replaceRange(
        selection,
        { line: startPos.line, ch: startPos.ch - 2 },
        { line: endPos.line, ch: endPos.ch + 2 },
      );
      cm.setSelection(
        { line: startPos.line, ch: startPos.ch - 2 },
        { line: endPos.line, ch: startPos.ch - 2 + selection.length },
      );
    } else {
      const unwrapped = selection.slice(2, -2);
      cm.replaceSelection(unwrapped);
      cm.setSelection(startPos, {
        line: endPos.line,
        ch: startPos.ch + unwrapped.length,
      });
    }
  } else {
    const wrapped = `**${selection}**`;
    cm.replaceSelection(wrapped);
    if (selection.length === 0) {
      cm.focus();
      cm.setCursor({ line: startPos.line, ch: startPos.ch + 2 });
    } else {
      cm.setSelection(
        { line: startPos.line, ch: startPos.ch + 2 },
        { line: endPos.line, ch: startPos.ch + 2 + selection.length },
      );
    }
  }
};

const applyItalic = (cm: any) => {
  const selection = cm.getSelection();
  const startPos = cm.getCursor('start');
  const endPos = cm.getCursor('end');

  const line = cm.getLine(startPos.line);
  const beforeStart1 = line.substring(Math.max(0, startPos.ch - 1), startPos.ch);
  const afterEnd1 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 1));
  const beforeStart2 = line.substring(Math.max(0, startPos.ch - 2), startPos.ch);
  const afterEnd2 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 2));
  const beforeStart3 = line.substring(Math.max(0, startPos.ch - 3), startPos.ch);
  const afterEnd3 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 3));

  const isBoldItalic = beforeStart3 === '***' && afterEnd3 === '***';
  const isItalic =
    (beforeStart1 === '*' && afterEnd1 === '*' && beforeStart2 !== '**' && afterEnd2 !== '**') ||
    (selection.startsWith('*') &&
      selection.endsWith('*') &&
      !selection.startsWith('**') &&
      selection.length > 2);

  if (isBoldItalic) {
    cm.replaceRange(
      `**${selection}**`,
      { line: startPos.line, ch: startPos.ch - 3 },
      { line: endPos.line, ch: endPos.ch + 3 },
    );
    cm.setSelection(
      { line: startPos.line, ch: startPos.ch - 1 },
      { line: endPos.line, ch: startPos.ch - 1 + selection.length },
    );
  } else if (isItalic) {
    if (beforeStart1 === '*' && afterEnd1 === '*' && beforeStart2 !== '**' && afterEnd2 !== '**') {
      cm.replaceRange(
        selection,
        { line: startPos.line, ch: startPos.ch - 1 },
        { line: endPos.line, ch: endPos.ch + 1 },
      );
      cm.setSelection(
        { line: startPos.line, ch: startPos.ch - 1 },
        { line: endPos.line, ch: startPos.ch - 1 + selection.length },
      );
    } else {
      const unwrapped = selection.slice(1, -1);
      cm.replaceSelection(unwrapped);
      cm.setSelection(startPos, {
        line: endPos.line,
        ch: startPos.ch + unwrapped.length,
      });
    }
  } else {
    const wrapped = `*${selection}*`;
    cm.replaceSelection(wrapped);
    if (selection.length === 0) {
      cm.focus();
      cm.setCursor({ line: startPos.line, ch: startPos.ch + 1 });
    } else {
      cm.setSelection(
        { line: startPos.line, ch: startPos.ch + 1 },
        { line: endPos.line, ch: startPos.ch + 1 + selection.length },
      );
    }
  }
};

const applyOrderedList = (cm: any) => {
  const cursor = cm.getCursor();
  const line = cm.getLine(cursor.line);

  if (line.match(/^\d+\.\s/)) {
    cm.replaceRange(
      line.replace(/^\d+\.\s/, ''),
      { line: cursor.line, ch: 0 },
      { line: cursor.line, ch: line.length },
    );
  } else {
    const cleanLine = line.replace(/^[-*]\s/, '');
    cm.replaceRange(
      '1. ' + cleanLine,
      { line: cursor.line, ch: 0 },
      { line: cursor.line, ch: line.length },
    );
  }
};

const applyUnorderedList = (cm: any) => {
  const cursor = cm.getCursor();
  const line = cm.getLine(cursor.line);

  if (line.match(/^[-*]\s/)) {
    cm.replaceRange(
      line.replace(/^[-*]\s/, ''),
      { line: cursor.line, ch: 0 },
      { line: cursor.line, ch: line.length },
    );
  } else {
    const cleanLine = line.replace(/^\d+\.\s/, '');
    cm.replaceRange(
      '- ' + cleanLine,
      { line: cursor.line, ch: 0 },
      { line: cursor.line, ch: line.length },
    );
  }
};

const Toolbar: FC = () => {
  const { mdeInstance } = useEditor();
  const [toolSelected, setToolSelected] = useState<ToolType>(null);
  const applyFormat = (tool: ToolType) => {
    if (!mdeInstance) return;
    const cm = mdeInstance.codemirror;

    setToolSelected(tool);

    switch (tool) {
      case 'bold':
        applyBold(cm);
        break;
      case 'italic':
        applyItalic(cm);
        break;
      case 'ordered-list':
        applyOrderedList(cm);
        break;
      case 'unordered-list':
        applyUnorderedList(cm);
        break;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5 }}>
      <IconButton
        disableRipple
        disableTouchRipple
        disableFocusRipple
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => applyFormat('bold')}
        sx={{
          color: toolSelected === 'bold' ? '#2F736E' : '#162936',
        }}
      >
        <FormatBoldIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
      <IconButton
        disableRipple
        disableTouchRipple
        disableFocusRipple
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => applyFormat('italic')}
        sx={{
          color: toolSelected === 'italic' ? '#2F736E' : '#162936',
        }}
      >
        <FormatItalicIcon sx={{ fontSize: '1rem' }} />
      </IconButton>

      <IconButton
        disableRipple
        disableTouchRipple
        disableFocusRipple
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => applyFormat('ordered-list')}
        sx={{
          color: toolSelected === 'ordered-list' ? '#2F736E' : '#162936',
        }}
      >
        <FormatListNumberedIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
      <IconButton
        disableRipple
        disableTouchRipple
        disableFocusRipple
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => applyFormat('unordered-list')}
        sx={{
          color: toolSelected === 'unordered-list' ? '#2F736E' : '#162936',
        }}
      >
        <FormatListBulletedIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Box>
  );
};

export default Toolbar;
